import { computed } from 'vue'
import { RoleCode } from '@prisma/client'
import { useRoleStore } from '~/store/roleStore'
import { useAuthStore } from '~/store/auth'

// Define capabilities
export const Capabilities = {
  DASHBOARD: {
    CREATE_ANNOUNCEMENT: 'dashboard.createAnnouncement',
    CREATE_NEW_EVENTS: 'dashboard.createNewEvents',
  },
  BUSINESS: {
    CREATE: 'business.create',
    CREATE_APPRAISAL_PROPOSAL_CIM: 'business.createAppraisalProposalCim',
    EDIT: 'business.edit',
    PROFILE_VIEW: 'business.profileView',
    LISTING_FILTER_TOGGLE: 'business.listingFilterToggle',
    BY_BROKER_FILTER: 'business.byBrokerFilter',
  },
  USERS: {
    ACCESS: 'users.access',
    CHANGE_OWNER_ROLE: 'users.changeOwnerRole',
  },
  TEMPLATES: {
    CREATE_CIM_PROPOSAL: 'templates.createCimProposal',
    EDIT_CIM_PROPOSAL: 'templates.editCimProposal',
    APPRAISAL_NDA_MENU: 'templates.appraisalNdaMenu',
  },
  SETTINGS: {
    WORKSPACE: 'settings.workspace',
    BILLING: 'settings.billing',
  },
  CONTRACTS: {
    DETAILS: 'contracts.details',
    ACTIVITY: 'contracts.activity',
    FILES: 'contracts.files',
    TRANSACTIONS: 'contracts.transactions',
  },
  PROFILE: {
    ACCESS: 'profile.access',
  },
  CIM_ACCESS: {
    CIM_REQUEST: 'cimAccessPage.cimRequest',
    AUTO_SEND_SETTINGS: 'cimAccessPage.autoSendSettings',
    CIM_SHARE_CREATE_USER: 'cimAccessPage.cimShareCreateUser',
    CIM_SHARE_APPROVE_ACCESS: 'cimAccessPage.cimShareApproveAccess',
    CIM_SHARE_DENY_ACCESS: 'cimAccessPage.cimShareDenyAccess',
  },
  BUSINESS_SHARING: {
    SUPPLEMENTARY_LINKS: 'businessSharing.supplementaryLinks',
  },
  PIPELINE: {
    MANAGE_STAGES: 'pipeline.manageStages',
    VIEW_BROKER_FILTER: 'pipeline.viewBrokerFilter',
  },
  CONTACT: {
    MANAGE_GROUPS: 'contact.manageGroups',
    IMPORT_CONTACTS: 'contact.importContacts',
  },
  SIDEBAR: {
    DASHBOARD: 'sidebar.dashboard',
    BUSINESS: 'sidebar.business',
    BUSINESS_STATS: 'sidebar.businessStats',
    PIPELINE: 'sidebar.pipeline',
    CONTRACTS: 'sidebar.contracts',
    TRUST_ACCOUNTING: 'sidebar.trustAccounting',
    CONTACTS: 'sidebar.contacts',
    TASKS: 'sidebar.tasks',
    TEMPLATES: 'sidebar.templates',
    AUTOMATIONS: 'sidebar.automations',
    CAMPAIGNS: 'sidebar.campaign',
    CALENDAR: 'sidebar.calendar',
    SETTINGS: 'sidebar.settings',
    USERS: 'sidebar.users',
    BILLING: 'sidebar.billing',
    RESOURCES: 'sidebar.resources',
  }

}

// Map of capabilities for each role
const roleCapabilities: Record<RoleCode, Record<string, true | 'own' | 'all'>> = {
  SUPPORT: {
    [Capabilities.BUSINESS.EDIT]: 'all',
    [Capabilities.BUSINESS.CREATE]: true,
    [Capabilities.BUSINESS.PROFILE_VIEW]: true,
    [Capabilities.USERS.ACCESS]: true,
    [Capabilities.USERS.CHANGE_OWNER_ROLE]: true,
    [Capabilities.TEMPLATES.CREATE_CIM_PROPOSAL]: true,
    [Capabilities.TEMPLATES.EDIT_CIM_PROPOSAL]: true,
    [Capabilities.TEMPLATES.APPRAISAL_NDA_MENU]: true,
    [Capabilities.SETTINGS.WORKSPACE]: true,
    [Capabilities.SETTINGS.BILLING]: true,
    [Capabilities.CONTRACTS.DETAILS]: true,
    [Capabilities.CONTRACTS.ACTIVITY]: true,
    [Capabilities.CONTRACTS.FILES]: true,
    [Capabilities.CONTRACTS.TRANSACTIONS]: true,
    [Capabilities.CIM_ACCESS.CIM_REQUEST]: true,
    [Capabilities.CIM_ACCESS.AUTO_SEND_SETTINGS]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_CREATE_USER]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_APPROVE_ACCESS]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_DENY_ACCESS]: true,
    [Capabilities.BUSINESS_SHARING.SUPPLEMENTARY_LINKS]: true,

    [Capabilities.BUSINESS.LISTING_FILTER_TOGGLE]: true,
    [Capabilities.BUSINESS.BY_BROKER_FILTER]: true,
    [Capabilities.PIPELINE.MANAGE_STAGES]: true,
    [Capabilities.CONTACT.MANAGE_GROUPS]: true,
    [Capabilities.CONTACT.IMPORT_CONTACTS]: true,

    // sidebar 
    [Capabilities.SIDEBAR.DASHBOARD]: true,
    [Capabilities.SIDEBAR.BUSINESS]: true,
    [Capabilities.SIDEBAR.BUSINESS_STATS]: true,
    [Capabilities.SIDEBAR.PIPELINE]: true,
    [Capabilities.SIDEBAR.CONTACTS]: true,
    [Capabilities.SIDEBAR.CONTRACTS]: true,
    [Capabilities.SIDEBAR.TRUST_ACCOUNTING]: true,
    [Capabilities.SIDEBAR.TASKS]: true,
    [Capabilities.SIDEBAR.TEMPLATES]: true,
    [Capabilities.SIDEBAR.AUTOMATIONS]: true,
    [Capabilities.SIDEBAR.CAMPAIGNS]: true,
    [Capabilities.SIDEBAR.CALENDAR]: true,
    [Capabilities.SIDEBAR.SETTINGS]: true,
    [Capabilities.SIDEBAR.USERS]: true,
    [Capabilities.SIDEBAR.BILLING]: true,
    [Capabilities.SIDEBAR.RESOURCES]: true,

    //Piepline
    [Capabilities.PIPELINE.VIEW_BROKER_FILTER]: true,
  },
  ADMIN: {
    [Capabilities.DASHBOARD.CREATE_ANNOUNCEMENT]: true,
    [Capabilities.DASHBOARD.CREATE_NEW_EVENTS]: true,
    [Capabilities.BUSINESS.CREATE]: true,
    [Capabilities.BUSINESS.CREATE_APPRAISAL_PROPOSAL_CIM]: true,
    [Capabilities.BUSINESS.EDIT]: 'all',
    [Capabilities.BUSINESS.BY_BROKER_FILTER]: true,
    [Capabilities.BUSINESS.PROFILE_VIEW]: true,
    [Capabilities.USERS.ACCESS]: true,
    [Capabilities.TEMPLATES.CREATE_CIM_PROPOSAL]: true,
    [Capabilities.TEMPLATES.EDIT_CIM_PROPOSAL]: true,
    [Capabilities.TEMPLATES.APPRAISAL_NDA_MENU]: true,
    [Capabilities.SETTINGS.WORKSPACE]: true,
    [Capabilities.CONTRACTS.DETAILS]: true,
    [Capabilities.CONTRACTS.ACTIVITY]: true,
    [Capabilities.CONTRACTS.FILES]: true,
    [Capabilities.CONTRACTS.TRANSACTIONS]: true,
    [Capabilities.PROFILE.ACCESS]: true,
    [Capabilities.CIM_ACCESS.CIM_REQUEST]: true,
    [Capabilities.CIM_ACCESS.AUTO_SEND_SETTINGS]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_CREATE_USER]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_APPROVE_ACCESS]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_DENY_ACCESS]: true,
    [Capabilities.BUSINESS_SHARING.SUPPLEMENTARY_LINKS]: true,
    [Capabilities.BUSINESS.LISTING_FILTER_TOGGLE]: true,
    [Capabilities.PIPELINE.MANAGE_STAGES]: true,
    [Capabilities.CONTACT.MANAGE_GROUPS]: true,
    [Capabilities.CONTACT.IMPORT_CONTACTS]: true,


    // sidebar 
    [Capabilities.SIDEBAR.DASHBOARD]: true,
    [Capabilities.SIDEBAR.BUSINESS]: true,
    [Capabilities.SIDEBAR.BUSINESS_STATS]: true,
    [Capabilities.SIDEBAR.PIPELINE]: true,
    [Capabilities.SIDEBAR.CONTACTS]: true,
    [Capabilities.SIDEBAR.CONTRACTS]: true,
    [Capabilities.SIDEBAR.TRUST_ACCOUNTING]: true,
    [Capabilities.SIDEBAR.TASKS]: true,
    [Capabilities.SIDEBAR.TEMPLATES]: true,
    [Capabilities.SIDEBAR.AUTOMATIONS]: true,
    [Capabilities.SIDEBAR.CAMPAIGNS]: true,
    [Capabilities.SIDEBAR.CALENDAR]: true,
    [Capabilities.SIDEBAR.SETTINGS]: true,
    [Capabilities.SIDEBAR.USERS]: true,
    [Capabilities.SIDEBAR.BILLING]: true,
    [Capabilities.SIDEBAR.RESOURCES]: true,
    
    //Piepline
    [Capabilities.PIPELINE.VIEW_BROKER_FILTER]: true,
  },
  OWNER: {
    [Capabilities.DASHBOARD.CREATE_ANNOUNCEMENT]: true,
    [Capabilities.DASHBOARD.CREATE_NEW_EVENTS]: true,
    [Capabilities.BUSINESS.CREATE]: true,
    [Capabilities.BUSINESS.CREATE_APPRAISAL_PROPOSAL_CIM]: true,
    [Capabilities.BUSINESS.EDIT]: 'all',
    [Capabilities.BUSINESS.BY_BROKER_FILTER]: true,
    [Capabilities.BUSINESS.PROFILE_VIEW]: true,
    [Capabilities.USERS.ACCESS]: true,
    [Capabilities.TEMPLATES.CREATE_CIM_PROPOSAL]: true,
    [Capabilities.TEMPLATES.EDIT_CIM_PROPOSAL]: true,
    [Capabilities.TEMPLATES.APPRAISAL_NDA_MENU]: true,
    [Capabilities.SETTINGS.WORKSPACE]: true,
    [Capabilities.SETTINGS.BILLING]: true,
    [Capabilities.CONTRACTS.DETAILS]: true,
    [Capabilities.CONTRACTS.ACTIVITY]: true,
    [Capabilities.CONTRACTS.FILES]: true,
    [Capabilities.PROFILE.ACCESS]: true,
    [Capabilities.CIM_ACCESS.CIM_REQUEST]: true,
    [Capabilities.CIM_ACCESS.AUTO_SEND_SETTINGS]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_CREATE_USER]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_APPROVE_ACCESS]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_DENY_ACCESS]: true,
    [Capabilities.BUSINESS_SHARING.SUPPLEMENTARY_LINKS]: true,
    [Capabilities.BUSINESS.LISTING_FILTER_TOGGLE]: true,
    //  crm
    [Capabilities.PIPELINE.MANAGE_STAGES]: true,
    [Capabilities.CONTACT.MANAGE_GROUPS]: true,
    [Capabilities.CONTACT.IMPORT_CONTACTS]: true,



    // sidebar 
    [Capabilities.SIDEBAR.DASHBOARD]: true,
    [Capabilities.SIDEBAR.BUSINESS]: true,
    [Capabilities.SIDEBAR.BUSINESS_STATS]: true,
    [Capabilities.SIDEBAR.PIPELINE]: true,
    [Capabilities.SIDEBAR.CONTACTS]: true,
    [Capabilities.SIDEBAR.CONTRACTS]: true,
    [Capabilities.SIDEBAR.TRUST_ACCOUNTING]: true,
    [Capabilities.SIDEBAR.TASKS]: true,
    [Capabilities.SIDEBAR.TEMPLATES]: true,
    [Capabilities.SIDEBAR.AUTOMATIONS]: true,
    [Capabilities.SIDEBAR.CAMPAIGNS]: true,
    [Capabilities.SIDEBAR.CALENDAR]: true,
    [Capabilities.SIDEBAR.SETTINGS]: true,
    [Capabilities.SIDEBAR.USERS]: true,
    [Capabilities.SIDEBAR.BILLING]: true,
    [Capabilities.SIDEBAR.RESOURCES]: true,

    //Piepline
    [Capabilities.PIPELINE.VIEW_BROKER_FILTER]: true,
  },

  BROKER: {
    [Capabilities.BUSINESS.CREATE]: true,
    [Capabilities.BUSINESS.CREATE_APPRAISAL_PROPOSAL_CIM]: true,
    [Capabilities.BUSINESS.EDIT]: 'own',
    [Capabilities.BUSINESS.BY_BROKER_FILTER]: true,
    [Capabilities.BUSINESS.PROFILE_VIEW]: true,
    [Capabilities.TEMPLATES.CREATE_CIM_PROPOSAL]: true,
    [Capabilities.TEMPLATES.EDIT_CIM_PROPOSAL]: 'own',
    [Capabilities.CONTRACTS.DETAILS]: true,
    [Capabilities.CONTRACTS.ACTIVITY]: true,
    [Capabilities.CONTRACTS.FILES]: true,
    [Capabilities.PROFILE.ACCESS]: true,
    [Capabilities.CIM_ACCESS.CIM_REQUEST]: true,
    [Capabilities.CIM_ACCESS.AUTO_SEND_SETTINGS]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_CREATE_USER]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_APPROVE_ACCESS]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_DENY_ACCESS]: true,
    [Capabilities.BUSINESS_SHARING.SUPPLEMENTARY_LINKS]: true,

    [Capabilities.BUSINESS.LISTING_FILTER_TOGGLE]: true,

    // sidebar 
    [Capabilities.SIDEBAR.DASHBOARD]: true,
    [Capabilities.SIDEBAR.BUSINESS]: true,
    [Capabilities.SIDEBAR.PIPELINE]: true,
    [Capabilities.SIDEBAR.CONTACTS]: true,
    [Capabilities.SIDEBAR.CONTRACTS]: true,
    [Capabilities.SIDEBAR.TASKS]: true,
    [Capabilities.SIDEBAR.TEMPLATES]: true,
    [Capabilities.SIDEBAR.CALENDAR]: true,
    [Capabilities.SIDEBAR.RESOURCES]: true,
  },
  MBS_BROKER: {
    [Capabilities.BUSINESS.CREATE]: true,
    [Capabilities.BUSINESS.CREATE_APPRAISAL_PROPOSAL_CIM]: true,
    [Capabilities.BUSINESS.EDIT]: 'own',
    [Capabilities.BUSINESS.BY_BROKER_FILTER]: true,
    [Capabilities.BUSINESS.PROFILE_VIEW]: true,
    [Capabilities.TEMPLATES.CREATE_CIM_PROPOSAL]: true,
    [Capabilities.TEMPLATES.EDIT_CIM_PROPOSAL]: 'own',
    [Capabilities.CONTRACTS.DETAILS]: true,
    [Capabilities.CONTRACTS.ACTIVITY]: true,
    [Capabilities.CONTRACTS.FILES]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_CREATE_USER]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_APPROVE_ACCESS]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_DENY_ACCESS]: true,
    [Capabilities.BUSINESS.LISTING_FILTER_TOGGLE]: true,
    [Capabilities.PROFILE.ACCESS]: true,
    [Capabilities.BUSINESS_SHARING.SUPPLEMENTARY_LINKS]: true,

    // sidebar
    [Capabilities.SIDEBAR.DASHBOARD]: true,
    [Capabilities.SIDEBAR.BUSINESS]: true,
    [Capabilities.SIDEBAR.PIPELINE]: true,
    [Capabilities.SIDEBAR.CONTACTS]: true,
    [Capabilities.SIDEBAR.CONTRACTS]: true,
    [Capabilities.SIDEBAR.TASKS]: true,
    [Capabilities.SIDEBAR.TEMPLATES]: true,
    [Capabilities.SIDEBAR.CALENDAR]: true,
    [Capabilities.SIDEBAR.RESOURCES]: true,
  },
  MBS_BROKER_RESTRICTED: {
    [Capabilities.BUSINESS.CREATE]: true,
    [Capabilities.BUSINESS.CREATE_APPRAISAL_PROPOSAL_CIM]: true,
    [Capabilities.BUSINESS.EDIT]: 'own',
    [Capabilities.BUSINESS.PROFILE_VIEW]: true,
    
    [Capabilities.TEMPLATES.CREATE_CIM_PROPOSAL]: true,
    [Capabilities.TEMPLATES.EDIT_CIM_PROPOSAL]: 'own',
    [Capabilities.CONTRACTS.DETAILS]: true,
    [Capabilities.CONTRACTS.ACTIVITY]: true,
    [Capabilities.CONTRACTS.FILES]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_CREATE_USER]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_APPROVE_ACCESS]: true,
    [Capabilities.CIM_ACCESS.CIM_SHARE_DENY_ACCESS]: true,
    // [Capabilities.BUSINESS.LISTING_FILTER_TOGGLE]: true,

    [Capabilities.PROFILE.ACCESS]: true,
    [Capabilities.BUSINESS_SHARING.SUPPLEMENTARY_LINKS]: true,

    // sidebar
    [Capabilities.SIDEBAR.DASHBOARD]: true,
    [Capabilities.SIDEBAR.BUSINESS]: true,
    [Capabilities.SIDEBAR.PIPELINE]: true,
    [Capabilities.SIDEBAR.CONTRACTS]: true,
    [Capabilities.SIDEBAR.TASKS]: true,
    [Capabilities.SIDEBAR.TEMPLATES]: true,
    [Capabilities.SIDEBAR.CALENDAR]: true,
    [Capabilities.SIDEBAR.RESOURCES]: true,
  },
  AUDITOR: {
    // View-only access to contracts and trust accounting
    [Capabilities.CONTRACTS.DETAILS]: true,
    [Capabilities.CONTRACTS.ACTIVITY]: true,
    [Capabilities.CONTRACTS.FILES]: true,
    [Capabilities.CONTRACTS.TRANSACTIONS]: true,

    // sidebar
    [Capabilities.SIDEBAR.CONTRACTS]: true,
    [Capabilities.SIDEBAR.TRUST_ACCOUNTING]: true,
  },
}

export function useRolePermissions() {
  const roleStore = useRoleStore()

  const isAdmin = computed(() => {
    return roleStore.roleCode === 'ADMIN' || roleStore.roleCode === 'OWNER'
  })

  const isOwner = computed(() => {
    return roleStore.roleCode === 'OWNER'
  })

  const isBroker = computed(() => {
    return roleStore.roleCode === RoleCode.BROKER || roleStore.roleCode == RoleCode.MBS_BROKER || roleStore.roleCode == RoleCode.MBS_BROKER_RESTRICTED
  })

  const isAuditor = computed(() => {
    return roleStore.roleCode === RoleCode.AUDITOR
  })
  const role = computed(() => roleStore.roleCode as RoleCode)
  const capabilities = computed(() => roleCapabilities[role.value] || {})

  // Returns true if user has access to the capability
  const can = (capability: string): boolean => {
    const val = capabilities.value[capability]
    return val === true || val === 'own' || val === 'all'
  }

  // Returns permission level: 'own' | 'all' | 'true' | 'none'
  const canWithScope = (capability: string): 'none' | 'own' | 'all' | 'true' => {
    const val = capabilities.value[capability]

    if (val === 'own') return 'own'
    if (val === 'all') return 'all'
    if (val === true) return 'true'
    return 'none'
  }

  return {
    isAdmin: isAdmin.value,
    isBroker: isBroker.value,
    isOwner: isOwner.value,
    isAuditor: isAuditor.value,

    can,
    canWithScope,
    Capabilities,

  }
}
